/*
  The quick add (snippets/quick-add-drawer.liquid): one drawer for the page, filled with the product's parts from
  sections/quick-add.liquid, which Shopify renders in the product's context when it is asked for on the product's
  address (https://shopify.dev/docs/api/ajax/section-rendering).

  A button with data-quick-add="<product address>" opens it. The button spins and is marked busy until the drawer is
  ready, which is when the answer's styles and scripts have loaded or failed (scripts inside inserted HTML never run,
  so they are added to the page here by loadAsset in assets/global.js, once per file whatever its version), the main
  picture has decoded and the payment-terms line has been drawn (each for half a second at most, together). A later
  click wins over one still loading, and a request that hangs ends after 15 seconds like a failed one. On a device
  that can hover, the pointer resting on a button for a moment, or the keyboard arriving at it with Tab, asks for the
  answer early: one early request at a time, none for a pointer that only passes over. A failed request, or a script
  that does not load, opens the drawer with a message and a way to try again. A stylesheet that does not load does
  not: it is logged, dropped, and asked for again by the next drawer. The header's View details leads to the product
  the drawer shows, at the variant chosen in it.

  The quick add and the cart drawer take each other's place: the one that goes stays where it is while the other comes
  in over it (close with `swap` in assets/dialog-window.js). Opened while the cart drawer is open, the quick add takes its
  place and offers a way back to it. After an add (the product form says so), Theme settings › Cart › After adding
  decides, as for every product form: the cart drawer takes the quick add's place, or the quick add closes and the
  page stays (a form that goes to the cart page has left it already). Closing the drawer empties it, once its fade has
  ended.
*/

if (!customElements.get('quick-add')) {
  const SECTION = 'quick-add';
  // How long the pointer rests on an opener before the answer is asked for early.
  const REST_TIME = 150;
  // How long the main picture's decoding and the payment-terms line's drawing are waited for.
  const DECODE_TIME = 500;

  // The answers kept for a minute (stock and prices move); a change to the cart makes them out of date (the "in cart"
  // count, the stock).
  const answers = sectionAnswers({ size: 20, life: 60000, clearOn: PUB_SUB_EVENTS.cartUpdate });

  // Settles like the promise, or rejects when the time runs out (AbortSignal.timeout: no timer of ours to clear).
  const within = (promise, time) =>
    new Promise((resolve, reject) => {
      const clock = AbortSignal.timeout(time);
      clock.addEventListener('abort', () => reject(clock.reason), { once: true });
      promise.then(resolve, reject);
    });

  const setBusy = (button, busy) => {
    if (!button) return;
    if (busy) {
      button.setAttribute('aria-disabled', 'true');
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-disabled');
      button.removeAttribute('aria-busy');
    }
  };

  class QuickAdd extends HTMLElement {
    connectedCallback() {
      this.host = this.querySelector('dialog-window');
      this.dialog = this.querySelector('dialog');
      this.content = this.querySelector('[data-quick-content]');
      this.back = this.querySelector('[data-quick-back]');
      this.details = this.querySelector('[data-quick-details]');
      this.drawerTitle = this.dialog?.getAttribute('aria-labelledby');
      if (!this.dialog || !this.content) return;
      this.controller = new AbortController();
      this.ticket = 0;
      const { signal } = this.controller;

      document.addEventListener('click', (event) => this.onOpenerClick(event), { signal });
      // Asking early: the pointer resting on an opener, or Tab arriving at it. Focus that comes back to an opener from
      // a closed drawer (or from a click) is no sign of interest, so only focus that Tab moved counts.
      document.addEventListener(
        'pointerover',
        (event) => event.pointerType === 'mouse' && this.rest(event.target.closest?.('[data-quick-add]')),
        { signal, passive: true }
      );
      document.addEventListener(
        'pointerout',
        (event) => event.pointerType === 'mouse' && this.leave(event.target, event.relatedTarget),
        { signal, passive: true }
      );
      document.addEventListener('keydown', (event) => (this.tabbed = event.key === 'Tab'), { signal, capture: true });
      document.addEventListener(
        'focusin',
        (event) => {
          const arrived = this.tabbed;
          this.tabbed = false;
          if (arrived) this.rest(event.target.closest?.('[data-quick-add]'));
        },
        { signal }
      );
      document.addEventListener('focusout', (event) => this.leave(event.target, event.relatedTarget), { signal });
      // The theme editor replaces sections, and their buttons, at any time.
      document.addEventListener('shopify:section:load', () => this.close(), { signal });

      this.dialog.addEventListener('click', (event) => this.onDrawerClick(event), { signal });
      this.dialog.addEventListener('dialog-window:close', () => this.onClose(), { signal });
      this.dialog.addEventListener('product-form:added', () => this.onAdded(), { signal });
      // A variant picked in the drawer (assets/product-info.js says which) is where View details leads.
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        const product = this.content.querySelector('product-info')?.dataset.url;
        if (data.sectionId !== SECTION || !product) return;
        this.details.href = data.variant ? `${product}?variant=${data.variant.id}` : product;
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.unsubscribe?.();
      clearTimeout(this.restTimer);
    }

    // Looked up each time: the theme editor can render the cart drawer again at any time.
    get cart() {
      return document.querySelector('cart-drawer');
    }

    urlOf(opener) {
      return opener.getAttribute('data-quick-add') || '';
    }

    // --- Opening ---------------------------------------------------------------------------------------------

    onOpenerClick(event) {
      const opener = event.target.closest?.('[data-quick-add]');
      if (!opener || event.defaultPrevented) return;
      // A click that asks for the link itself (a new tab or window, a download) keeps it.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.cancelRest();
      if (opener.getAttribute('aria-disabled') === 'true') return;
      this.show(opener, this.urlOf(opener));
    }

    // The pointer or the keyboard has come to an opener: if it stays for a moment, the answer is asked for. Not on
    // devices that cannot hover, and not in the theme editor, where the answer changes with the settings.
    rest(opener) {
      if (!opener || opener === this.resting || !fineHover.matches || window.Shopify?.designMode) return;
      this.cancelRest();
      this.resting = opener;
      this.restTimer = setTimeout(() => {
        this.resting = null;
        this.prefetch(this.urlOf(opener));
      }, REST_TIME);
    }

    // It went somewhere else before the moment was over.
    leave(from, to) {
      const opener = from.closest?.('[data-quick-add]');
      if (opener && opener === this.resting && !opener.contains(to)) this.cancelRest();
    }

    cancelRest() {
      clearTimeout(this.restTimer);
      this.resting = null;
    }

    // One early request at a time: while one is on its way, others wait for the next time the pointer rests.
    prefetch(url) {
      if (this.prefetching) return;
      this.prefetching = this.answer(url)
        .catch(() => {})
        .finally(() => (this.prefetching = null));
    }

    // The section request for a product address.
    answerUrl(url) {
      return `${url}${url.includes('?') ? '&' : '?'}section_id=${SECTION}`;
    }

    // The answer for a product address: a kept one, one already on its way, or a new request. A request is never
    // cancelled for a newer click, which may be waiting for the same answer (an answer nobody wants any more is dropped
    // by its ticket); one that hangs is ended after REQUEST_TIMEOUT, when the last waiting for it has given up.
    answer(url) {
      return answers.get(this.answerUrl(url), AbortSignal.timeout(REQUEST_TIMEOUT));
    }

    // Asks for the product and puts it in the drawer, without opening it. Resolves true when the latest request ended
    // with the product in place. A failure shows the failure message in the drawer instead, opening it when closed.
    async load(url, busyButton, returnTo) {
      const ticket = ++this.ticket;
      setBusy(this.busyButton, false);
      this.busyButton = busyButton;
      setBusy(busyButton, true);
      try {
        const nodes = await this.prepare(await this.answer(url));
        if (ticket !== this.ticket) return false;
        this.content.replaceChildren(...nodes);
        this.details.href = url;
        // The picture and the payment-terms line are waited for together and only so long: a slow one finishes drawing
        // in the open drawer.
        await Promise.all([this.pictureDecoded(), this.termsDrawn()]);
        if (ticket !== this.ticket) return false;
        this.name(this.content.querySelector('product-info')?.dataset.titleId);
        return true;
      } catch (error) {
        // A kept answer that did not make a drawer is not offered again by "Try again".
        answers.drop(this.answerUrl(url));
        if (ticket === this.ticket) {
          console.warn('Core: the quick add could not be loaded', error);
          this.fail(url, returnTo);
        }
        return false;
      } finally {
        if (ticket === this.ticket) {
          setBusy(busyButton, false);
          this.busyButton = null;
        }
      }
    }

    async show(opener, url) {
      if (!(await this.load(url, opener, opener))) return;
      this.open(opener);
      this.content.querySelector('product-component')?.dispatchViewEvent?.();
    }

    // The answer's parts: its styles and scripts are added to the page when it lacks them (and waited for), and the
    // rest is handed back to be put in the drawer.
    async prepare(text) {
      const page = new DOMParser().parseFromString(text, 'text/html');
      if (!page.querySelector('product-info')) throw new Error('The answer holds no product');
      // DOMParser parses with scripting off, so what a <noscript> holds becomes real elements (a <style> in it would
      // hide the picker). This page runs scripts: nothing meant for a page without them belongs in the drawer.
      for (const fallback of page.querySelectorAll('noscript')) fallback.remove();
      const wanted = [];
      for (const link of page.querySelectorAll('link[rel="stylesheet"][href]')) {
        wanted.push(loadAsset(link.getAttribute('href')));
        link.remove();
      }
      for (const script of page.querySelectorAll('script[src]')) {
        wanted.push(loadAsset(script.getAttribute('src')));
        script.remove();
      }
      await within(Promise.all(wanted), REQUEST_TIMEOUT);
      const section = page.querySelector('.shopify-section') ?? page.body;
      return [...section.childNodes];
    }

    pictureDecoded() {
      const image = this.mainImage();
      return image ? within(image.decode(), DECODE_TIME).catch(() => {}) : Promise.resolve();
    }

    // Shopify's payment-terms element (the installments line) draws itself into its shadow root once it is in the page,
    // and says nothing when done: its root getting content is the sign. A store without installments prints no element.
    termsDrawn() {
      const terms = this.content.querySelector('shopify-payment-terms');
      if (!terms) return Promise.resolve();
      const drawn = customElements.whenDefined('shopify-payment-terms').then(
        () =>
          new Promise((resolve) => {
            const root = terms.shadowRoot;
            if (!root || root.childElementCount) return resolve();
            const watcher = new MutationObserver(() => {
              watcher.disconnect();
              resolve();
            });
            watcher.observe(root, { childList: true });
          })
      );
      return within(drawn, DECODE_TIME).catch(() => {});
    }

    mainImage() {
      return (
        this.content.querySelector('.product-gallery .is-active img') ??
        this.content.querySelector('.quick-add-product__media img')
      );
    }

    // Once the product is in, its own heading names the drawer; without one, the drawer's own title does.
    name(headingId) {
      this.dialog.setAttribute('aria-labelledby', headingId || this.drawerTitle);
    }

    // Opens the drawer, in the cart drawer's place when that is open: they never stack, and the back button shows.
    // Focus goes back to the opener when the drawer closes, or, when that was a card inside the cart drawer, which is
    // closed now, to the cart's own button.
    open(opener) {
      const cart = this.cart;
      const fromCart = Boolean(cart?.dialog?.open);
      if (this.back) this.back.hidden = !fromCart;
      this.returnTo = fromCart ? document.getElementById('cart-icon-bubble') || opener : opener;
      if (fromCart) cart.close(true);
      this.host.open(this.returnTo);
    }

    // The failure message in place of the content. A closed drawer opens for it (an open one keeps its place, its back
    // button and its way back), and the retry button takes focus so the shopper can press it again.
    fail(url, returnTo) {
      const template = this.dialog.querySelector('template[data-quick-failure]');
      this.name(null);
      this.content.replaceChildren(template.content.cloneNode(true));
      this.details.href = url;
      this.retryUrl = url;
      if (!this.dialog.open) this.open(returnTo);
      this.content.querySelector('[data-quick-retry]')?.focus();
    }

    // --- Inside the drawer -------------------------------------------------------------------------------------

    onDrawerClick(event) {
      if (event.target.closest('[data-quick-back]')) {
        this.toCart();
        return;
      }
      const retry = event.target.closest('[data-quick-retry]');
      if (retry && retry.getAttribute('aria-disabled') !== 'true') this.retry(retry);
    }

    // The failure message's button asks again. The drawer is open already, so the content is swapped in place, and the
    // product's heading takes the focus the button had, so its name is read.
    async retry(button) {
      if (!(await this.load(this.retryUrl, button, null))) return;
      const heading = document.getElementById(this.content.querySelector('product-info')?.dataset.titleId);
      heading?.setAttribute('tabindex', '-1');
      heading?.focus();
      this.content.querySelector('product-component')?.dispatchViewEvent?.();
    }

    // Added to the cart: the cart drawer takes the quick add's place when Theme settings › Cart › After adding opens
    // it, and otherwise the quick add closes. An add that ends after the shopper closed the drawer opens nothing.
    onAdded() {
      if (!this.dialog.open) return;
      if (this.cart?.opensAfterAdd) this.toCart();
      else this.close();
    }

    // The cart drawer takes the quick add's place: back to the cart the quick add replaced, or after an add. When the
    // cart drawer closes, focus goes where the quick add's would have gone.
    toCart() {
      const cart = this.cart;
      if (!cart?.ready) {
        this.close();
        return;
      }
      this.host.close(true);
      cart.open(this.returnTo);
    }

    close() {
      this.host?.close();
    }

    // Closed: once the fade has ended (the animations are done at once under reduced motion), the content goes, so
    // nothing in it keeps listening for the cart or asking for stock.
    onClose() {
      // A load still on its way (a "Try again", say) must not fill or reopen the closed drawer.
      const ticket = ++this.ticket;
      setBusy(this.busyButton, false);
      this.busyButton = null;
      const fades = this.dialog.getAnimations({ subtree: true }).map((animation) => animation.finished);
      Promise.allSettled(fades).then(() => {
        if (this.dialog.open || ticket !== this.ticket) return;
        this.content.replaceChildren();
        this.name(null);
      });
    }
  }

  customElements.define('quick-add', QuickAdd);
}
